'use client'

import { Button, Text } from '@repo/ui'
import { useEffect } from 'react'

import { captureError } from '@/lib/observability/errors'

/**
 * Route-level boundary for render and data throws below the root layout — the common case, which
 * `global-error.tsx` never sees. Reachable via a render throw in the client tree or a throw from
 * `providers.tsx`'s `headers()` read.
 *
 * It does **not** catch the candidate cache expiring: that throw surfaces inside `useQuery`, and no
 * `throwOnError` is set anywhere in the app, so React Query captures it as `isError`. `LiquidationTable`
 * owns that state and renders it inline.
 */
export default function Error({
  error,
  reset
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    captureError(error)
  }, [error])

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
      <Text size="sm" className="text-foreground">
        Could not load the console
      </Text>
      <Text size="xs" className="text-typography-secondary max-w-sm">
        Something failed while rendering this page. Retry, or reload if it persists.
      </Text>
      {/* `reset()`, not `router.refresh()`: refresh refetches server data without re-mounting the
          errored subtree, so a client-side throw would leave the button doing nothing visible. */}
      <Button size="sm" className="mt-2" onClick={reset}>
        Retry
      </Button>
    </div>
  )
}
